<template>
  <div class="mom-subapp">
    <div v-if="loading" class="mom-subapp__status">
      <a-spin size="large" :tip="loadingStage" />
    </div>
    <div v-if="loadFailed" class="mom-subapp__status mom-subapp__status--error">
      <a-icon type="warning" />
      <span>MOM 加载失败</span>
      <span class="mom-subapp__status-detail">{{ loadErrorMessage }}</span>
      <a-button type="primary" @click="retry">重试</a-button>
    </div>
    <iframe
      v-if="initialUrl"
      :key="renderKey"
      ref="momFrame"
      class="mom-subapp__frame"
      :src="initialUrl"
      title="MOM子应用"
      frameborder="0"
      @load="onFrameLoad"
    ></iframe>
    <div v-else class="mom-subapp__empty">MOM子应用地址未配置</div>
  </div>
</template>

<script>
import Cookies from 'js-cookie'
import {
  MOM_IFRAME_AUTH_EXPIRED,
  MOM_IFRAME_ERROR,
  MOM_IFRAME_MOUNTED,
  MOM_IFRAME_NAVIGATE,
  MOM_IFRAME_READY,
  buildMomVue3Url,
  createMomIframeInitMessage,
  findAllowedNavigation,
  formatMomLoadError,
  getUnexpectedNavigationError,
  isTrustedMomVue3Url
} from '@/utils/iframe-mom'
import { getMomBootstrap } from '@/utils/mom-bootstrap'

export default {
  name: 'MomSubapp',
  props: {
    routeMeta: {
      type: Object,
      default: null
    },
    routeQuery: {
      type: Object,
      default: null
    }
  },
  data () {
    const routeMeta = this.routeMeta || this.$route.meta || {}
    const routeQuery = this.routeQuery || this.$route.query || {}
    const outerLink = routeMeta.outerLink || routeMeta.externalLinkUrl || ''
    const candidateUrl = buildMomVue3Url(
      process.env.VUE_APP_MOM_SUBAPP_URL,
      outerLink,
      routeQuery
    )
    const trustedUrl = isTrustedMomVue3Url(
      candidateUrl,
      process.env.VUE_APP_MOM_SUBAPP_URL,
      window.location.href
    )
    const initialUrl = trustedUrl ? candidateUrl : ''
    return {
      initialUrl,
      authExpiredHandled: false,
      frameOrigin: trustedUrl
        ? new URL(initialUrl, window.location.href).origin
        : '',
      loadFailed: !trustedUrl,
      loadErrorMessage: trustedUrl ? '' : 'MOM 子应用地址不受信任',
      loading: trustedUrl,
      loadingStage: '正在启动 MOM...',
      loadingTimer: null,
      bridgeReady: false,
      appMounted: false,
      renderKey: 0
    }
  },
  mounted () {
    window.addEventListener('message', this.onFrameMessage)
    if (this.initialUrl) this.startLoadTimeout()
  },
  beforeDestroy () {
    window.removeEventListener('message', this.onFrameMessage)
    this.clearLoadTimeout()
  },
  methods: {
    onFrameLoad () {
      if (this.appMounted) return
      this.loading = true
      this.loadFailed = false
      this.loadingStage = 'MOM 页面已加载，正在初始化...'
      this.sendInitialization()
      this.startLoadTimeout(30000)
    },
    onFrameMessage (event) {
      const frame = this.$refs.momFrame
      if (!frame || event.source !== frame.contentWindow) return
      if (event.origin !== this.frameOrigin) return
      if (event.data && event.data.type === MOM_IFRAME_NAVIGATE) {
        this.navigateHostTab(event.data.request)
        return
      }
      if (event.data && event.data.type === MOM_IFRAME_READY) {
        this.bridgeReady = true
        if (this.appMounted) return
        this.loading = true
        this.loadFailed = false
        this.loadingStage = 'MOM 正在初始化...'
        this.sendInitialization()
        this.startLoadTimeout(30000)
        return
      }
      if (event.data && event.data.type === MOM_IFRAME_MOUNTED) {
        this.bridgeReady = true
        this.appMounted = true
        this.clearLoadTimeout()
        this.loading = false
        this.loadFailed = false
        return
      }
      if (event.data && event.data.type === MOM_IFRAME_ERROR) {
        this.clearLoadTimeout()
        this.loading = false
        this.loadFailed = true
        this.loadErrorMessage = 'MOM 子应用初始化失败，请重试'
        return
      }
      if (
        event.data &&
        event.data.type === MOM_IFRAME_AUTH_EXPIRED &&
        !this.authExpiredHandled
      ) {
        this.authExpiredHandled = true
        this.$store.dispatch('Logout').then(() => {
          window.location.reload()
        })
      }
    },
    navigateHostTab (request) {
      const location = findAllowedNavigation(this.$router.options.routes, request)
      if (!location) {
        console.warn('[MOM iframe] 已拒绝未授权的页签导航请求', request)
        return
      }
      try {
        const navigation = this.$router.push(location)
        if (navigation && typeof navigation.catch === 'function') {
          navigation.catch(error => {
            const unexpectedError = getUnexpectedNavigationError(error)
            if (unexpectedError) {
              console.error('[MOM iframe] 打开父应用页签失败', unexpectedError)
            }
          })
        }
      } catch (error) {
        const unexpectedError = getUnexpectedNavigationError(error)
        if (unexpectedError) {
          console.error('[MOM iframe] 打开父应用页签失败', unexpectedError)
        }
      }
    },
    sendInitialization () {
      const frame = this.$refs.momFrame
      if (!frame || !frame.contentWindow) return
      const isSystemLogin = localStorage.getItem('isFromSystemLogin') === 'true'
      const localToken = localStorage.getItem('token') || ''
      frame.contentWindow.postMessage(
        createMomIframeInitMessage(
          isSystemLogin ? localToken : Cookies.get(process.env.VUE_APP_XSRF_NAME),
          isSystemLogin ? localToken : Cookies.get('Admin-Token'),
          getMomBootstrap()
        ),
        this.frameOrigin
      )
    },
    retry () {
      this.refresh()
    },
    refresh () {
      this.loading = true
      this.loadFailed = false
      this.loadErrorMessage = ''
      this.loadingStage = '正在重新加载 MOM...'
      this.bridgeReady = false
      this.appMounted = false
      this.renderKey += 1
      this.startLoadTimeout()
    },
    startLoadTimeout (duration = 15000) {
      this.clearLoadTimeout()
      this.loadingTimer = setTimeout(() => {
        this.loadingTimer = null
        if (!this.loading) return
        if (this.bridgeReady) {
          this.loading = false
          this.loadFailed = false
          console.warn(`[MOM iframe] 子应用初始化时间较长，已继续显示页面：${this.initialUrl}`)
          return
        }
        this.loading = false
        this.loadFailed = true
        this.loadErrorMessage = formatMomLoadError(
          this.loadingStage.replace(/\.\.\.$/, ''),
          this.initialUrl
        )
      }, duration)
    },
    clearLoadTimeout () {
      if (!this.loadingTimer) return
      clearTimeout(this.loadingTimer)
      this.loadingTimer = null
    }
  }
}
</script>

<style lang="less" scoped>
.mom-subapp {
  position: relative;
  width: 100%;
  height: 100%;
  min-height: calc(100vh - 72px);
  background: #fff;
}

.mom-subapp__frame {
  display: block;
  width: 100%;
  height: 100%;
  min-height: calc(100vh - 72px);
  border: 0;
}

.mom-subapp__empty {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 240px;
  color: #999;
}

.mom-subapp__status {
  position: absolute;
  z-index: 2;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fff;
}

.mom-subapp__status--error {
  flex-direction: column;
  gap: 12px;
  color: #f5222d;
}

.mom-subapp__status-detail {
  max-width: 80%;
  color: #666;
  text-align: center;
  word-break: break-all;
}
</style>
